import { Injectable }                from "@angular/core";
import * as _                        from "lodash";
import { BehaviorSubject }           from "rxjs";
import { ApiService }                from "./api.service";
import { environment }               from "../../environments/environment";
import { AppStatus, emptyAppStatus } from "../@models/app-status";
import { allThemes }                 from "../@models/themes/theme.fun";
import { shuffle }                   from "../@models/core.fun";
import { MenuPage }                  from "../../shared-gen/Model/Menus/MenuPage";
import { ProductBase }               from "../../shared-gen/Model/Products/ProductBase";
import { Product }                   from "../../shared-gen/Model/Products/Product";
import { VendingStatusFun }          from "../../shared/vending/vending-status.fun";
import { equalsDeep }                from "../../shared/utils/core-utils";

/**
 * This services provides the current app status.
 * It always keeps the status updated to the state of the server.
 */
@Injectable({
	providedIn: 'root'
})
export class AppStatusService {

	appStatus: BehaviorSubject<AppStatus> = new BehaviorSubject<AppStatus>(emptyAppStatus());
	private lastAppStatus: AppStatus = emptyAppStatus();
	private isSynchronizing = false;
	private lastStatusMd5 = '';
	private readonly defaultStartPageColumns = 5;
	private refreshMenuAndSelections = true;

	private startPage: MenuPage;

	constructor(private api: ApiService) {
	}

	setSelectedProduct(product: ProductBase | Product) {
		this.reportUserActivity();
		const newAppStatus = _.cloneDeep(this.lastAppStatus);
		newAppStatus.selectedProduct = _.cloneDeep(product);
		this.applyStatus(newAppStatus);
	}

	/**
	 * Reports user activity to the server, to reset the inactivity timer.
	 */
	async reportUserActivity() {
		await this.api.deviceApi.userAction();
	}

	/**
	 * Keep the app status in sync with the server.
	 * When a timeout or error occurs, the vending status switches to offline and it is tried again.
	 */
	async startSyncWithServer() {
		if (this.isSynchronizing)
			return;
		this.isSynchronizing = true;
		await this.loopSyncWithServer();
	}

	private async loopSyncWithServer() {
		const startTime = Date.now();
		if (await this.updateFromServer()) {
			// Query again immediately (no problem because of long polling)
			window.setTimeout(() => this.loopSyncWithServer(), 0);
		} else {
			this.refreshMenuAndSelections = true; // Maybe a restart; force updating menu and selections
			const endTime = Date.now();
			console.log(`Update TIMEOUT after ${endTime - startTime} ms`);
			const newAppStatus = _.cloneDeep(this.lastAppStatus) as AppStatus;
			newAppStatus.deviceStatus.VendingStatus = VendingStatusFun.offline();
			this.applyStatus(newAppStatus, true);
			// Wait shortly before next try
			window.setTimeout(() => this.loopSyncWithServer(), environment.webApi.retryAfterTimeoutMs);
		}
	}

	/**
	 * Updates the app status from the server by getting the device status.
	 * When successfull, true is returned, otherwise false.
	 */
	private async updateFromServer(): Promise<boolean> {
		const statusPromise = this.api.deviceApi.getStatusDelayed(this.lastStatusMd5);
		try {
			const newAppStatus = _.cloneDeep(this.lastAppStatus) as AppStatus;
			// Update menu and selections, when required
			if (this.refreshMenuAndSelections) {
				newAppStatus.menuOriginal = await this.api.deviceApi.getMenu();
				newAppStatus.selections = await this.api.deviceApi.getSelections();
				this.refreshMenuAndSelections = false;
			}
			// Update device status
			const statusResponse = await statusPromise;
			newAppStatus.deviceStatus = statusResponse[1];
			newAppStatus.theme = allThemes.find(it => it.id === newAppStatus.deviceStatus.UIConfig.AppTheme);
			if (!newAppStatus.theme) {
				console.error(`Unknown theme ${newAppStatus.deviceStatus.UIConfig.AppTheme}`);
				return false;
			}
			this.lastStatusMd5 = statusResponse[0];
			this.applyStatus(newAppStatus);
			return true;
		} catch (ex) {
			return false;
		}
	}

	/**
	 * Publishes the given status, when it is different to the current one.
	 */
	private applyStatus(newAppStatus: AppStatus, forceUpdate = false) {
		if (forceUpdate || false === equalsDeep(this.appStatus.value, newAppStatus)) {
			console.log(new Date() + " APPSTATUS");
			this.lastAppStatus = newAppStatus;
			this.appStatus.next(newAppStatus);
		}
	}

	fireInactiveHooks() {
		console.info("Fire inactive hooks");
		this.buildStartpage();
	}

	getStartPage() {
		const menu = this.appStatus.getValue().menu;
		if (!menu.AutoStartPage) return;
		if (!this.startPage) {
			console.log("Need to rebuild start page");
			this.buildStartpage();
		}
		return this.startPage;
	}

	private buildStartpage() {
		const menu = this.appStatus.getValue().menu;
		if(!menu.AutoStartPage) return;
		let startPage: MenuPage = {
			Name: "", // TODO startPage Name?
			Columns: menu.AutoStartPageColumns || this.defaultStartPageColumns,
			Cells: null,
			AutoScroll: true
		};

		let cells = [];
		for (let page of menu.Pages) {
			for (let cell of page.Cells) {
				if (cell) {
					if (cells.findIndex(it => {
						return it.Product.ID === cell.Product.ID;
					}) === -1) {
						cells.push(cell);
					}
				}
			}
		}

		// Random order
		cells = shuffle(cells);

		startPage.Cells = cells;

		this.startPage = startPage;
	}

}
